import { defineStore } from 'pinia';
import { ref } from 'vue';
import jsonFetch from '@globals/jsonFetch';
import { getPermissionStatus } from '@globals/Permissions';
import { HelloResponse } from '@globals/types/Hello';

export interface TutorialVideo {
	title: string;
	name: string;
	link?: string;
	linkLabel?: string;
	wasRated?: boolean;
}

export interface TutorialVideoTrigger {
	body: string;
	header: string;
	videos: TutorialVideo[];
}

export interface TutorialModal {
	name: string;
	regexp: RegExp;
	important: boolean;
	displayed: boolean;
	skipped: boolean;
}

export const useCustomerOnboardingStore = defineStore('customerOnboarding', () => {
	const state = {
		/**
		 * Poszczególne ekrany onboardingu do wyświetlenia.
		 */
		steps: ref<{
			name: string;
			percentageValue: number;
		}[]>([]),

		/**
		 * Ile razy został wyświetlony ekran "Dzień dobry w Oferteo"?
		 */
		displayCount: ref(0),

		/**
		 * Szczegóły profilu użytkownika, które są wykorzystywane do wyświetlania ekranów onboardingowych.
		 * Głównie chodzi o to, czy użytkownik ma zdjęcia, opis, opinie, itp. oraz statystyki pokazywane
		 * na kafelku znajdującym się na powitalnym/końcowym ekranie.
		 */
		profileDetails: ref({
			yearsOnMarket: 0,
			offersCount: 0,
			hasPhotos: false,
			hasDescription: false,
			hasReviews: false,
		}),

		/**
		 * Czy konto użytkownika wymaga weryfikacji w danej sesji?
		 */
		needsVerification: ref(false),

		/**
		 * Czy pominąć początkowy ekran przy weryfikacji?
		 */
		skipVerificationIntro: ref(false),

		tutorialVideos: ref<{
			[key: string]: TutorialVideoTrigger;
		}>({}),

		globalTutorialVideo: ref<{
			source: string;
			videos: TutorialVideo[];
		}>({}),

		tutorialModals: ref<TutorialModal[]>([]),
		lastModalDate: ref(0),

		/**
		 * Lista nieprzeczytanych tutoriali
		 */
		tutorials: ref<string[]>([]),

		/**
		 * Lista kroków do uzupełnienia profilu
		 */
		onboardingTodo: ref<string[]>([]),

		/**
		 * Czy darmowe odkrycia za uzupełnienie profilu zostały odebrane?
		 */
		onboardingFreeUncoversClaimed: ref(false),

		/**
		 * Czy pobraliśmy kroki do uzupełnienia profilu?
		 */
		onboardingIsLoaded: ref(false),

		/**
		 * Adres do regulaminu onboardingu
		 */
		onboardingTosLink: ref(''),
	};

	const actions = {
		/**
		 * Uaktualnia dane dotyczące ekranów onboardingowych z początkowej odpowiedzi serwera.
		 */
		update(rsvp: HelloResponse) {
			// Wyświetl informację o konieczności weryfikacji Autenti zamiast onboardingu.
			if (rsvp.flags.includes('needsVerification')) {
				state.needsVerification.value = true;
				actions.showModal('autentiVerification');
			}

			// rsvp.user.videos = {
			// 	accountRecharge: {
			// 		header: 'Szukasz zleceń?',
			// 		videos: [
			// 			{
			// 				name: 'rfpList',
			// 				title: 'Szukasz zleceń?',
			// 				link: 'https://www.wp.pl/',
			// 				wasRated: false,
			// 			},
			// 			{
			// 				name: 'newCustomer',
			// 				title: 'Nowi klienci?',
			// 				link: 'https://www.wp.pl/',
			// 				linkLabel: 'Zobacz B',
			// 				wasRated: false,
			// 			},
			// 		],
			// 		body: 'Filip pokazuje, jak korzystać z listy zapytań! Obejrzyj wideo!',
			// 	},
			// };

			// Pobierz wideotutoriale.
			if (rsvp.user?.videos) {
				state.tutorialVideos.value = rsvp.user.videos;
			}

			// Pobierz informacje o nieprzeczytanych tutorialach.
			if (rsvp.user?.tutorials) {
				state.tutorials.value = rsvp.user.tutorials;
			}

			// Pobierz kroki onboardingu.
			if (!rsvp.user?.onboarding) { return; }
			state.steps.value = rsvp.user.onboarding.onboardingSteps;
			state.displayCount.value = rsvp.user.onboarding.allowDisablingGoodMorning ? 10 : 0;
			state.profileDetails.value = {
				yearsOnMarket: rsvp.user.yearsOnMarket || 0,
				offersCount: rsvp.user.placedOffersCount || 0,
				hasPhotos: rsvp.user.hasPhotos || false,
				hasDescription: rsvp.user.hasDescription || false,
				hasReviews: rsvp.user.hasMarks || false,
			};
		},

		/**
		 * Usuń tutorial z listy nieprzeczytanych.
		*/
		removeTutorial(name: string) {
			const idx = state.tutorials.value.findIndex((i: string) => i === name);
			if (idx >= 0) { state.tutorials.value.splice(idx, 1); }
		},

		/**
		 * Uaktualnia stan wypełnienia profilu.
		 */
		async updateOnboardingSteps() {
			try {
				const rsvp = await jsonFetch<{
					todo: string[],
					onboardingFreeUncoversClaimed: boolean,
					tosLink: string,
				}>('/myoferteo/konto-json/onboarding-completion-info.do');

				state.onboardingTodo.value = rsvp.todo || [];
				state.onboardingFreeUncoversClaimed.value = rsvp.onboardingFreeUncoversClaimed;
				state.onboardingIsLoaded.value = true;
				state.onboardingTosLink.value = rsvp.tosLink || '/public/docs/regulamin_3_darmowe_odkrycia.pdf';
			} catch (err) { }
		},

		/**
		 * Sprawdza, czy na danym URL-u można wyświetlić ekran onboardingowy zamiast zawartości strony.
		 */
		canShowOnboarding(href: string) {
			// return true;
			if (href.includes('/moje-zapytania') || href.includes('/formularz-kontaktowy')) { return false; }
			if (href.includes('goToAutenti=true')) { state.needsVerification.value = true; }
			if (state.needsVerification.value && href.includes('/konto') && !href.includes('/zapytanie')) { return true; }
			if (!state.steps.value.length) { return false; }

			const result = href.includes('/zapytania') || href.includes('/kontakty') || href.endsWith('/konto');
			if (result) { actions.showModal('onboarding'); }
			return result;
		},

		/**
		 * Pobiera i ustawia listę modali, które mogą być wyświetlone w trakcie korzystania z serwisu.
		 */
		async getModalList() {
			let rsvp;
			const mock = document.location.href.includes('debugMode=true');

			try {
				if (!mock) {
					rsvp = await jsonFetch<{
						lastModalDate: number;
						modals: {
							name: string;
							regexp: string;
							important: boolean;
							onlyDesktop?: boolean;
							onlyMobile?: boolean;
							onlyWeb?: boolean;
							onlyApp?: boolean;
						}[];
					}>('/public/json/modal/get-modal-list.do');
				} else {
					// Mock dla testów - zawiera wszystkie dostępne modale.
					rsvp = {
						modals: [
							{ name: 'onboardingIntro', regexp: '^((?!konto\/zapytanie).)*$', important: false },
							{ name: 'googleRate', regexp: '^((?!konto\/zapytanie).)*$', onlyWeb: true, important: false },
							{ name: 'appRate', regexp: '^((?!konto\/zapytanie).)*$', onlyApp: true, important: false },
							{ name: 'pushNotifModal', regexp: '^((?!konto\/zapytanie).)*$', important: false },
							{ name: 'rfpValuationIntro', regexp: '^\/konto\/zapytanie.*$', important: false },
							{ name: 'apRecommendList', regexp: '^\/konto\/(zapytania|profil)(?!\/promowanie).*$', important: false },
							{ name: 'apRecommendChat', regexp: '^\/konto\/zapytanie.*$', important: false },
							{ name: 'promoFillProfile', regexp: '^\/konto\/profil\/promowanie.*$', important: false },
							{ name: 'oexProfilePhoto', regexp: '^\/konto\/(terminarz|profil).*$', important: false },
							{ name: 'oexSmsNotifications', regexp: '^\/konto\/terminarz.*$', important: false },
							{ name: 'promoEnable', regexp: '^\/konto\/profil.*$', important: false }, // na końcu, bo się to pojawia dopiero po akcji użytkownika - inaczej te modale z /profil by nie weszły
						],
						lastModalDate: 0,
					};
				}

				if (!state.lastModalDate.value) {
					state.lastModalDate.value = rsvp.lastModalDate || 0;
				}

				const isApp = typeof wrapee !== 'undefined';
				const isMobile = window.innerWidth < 640;

				rsvp.modals = rsvp.modals.filter((m) => {
					if (m.onlyWeb && isApp) { return false; }
					if (m.onlyApp && !isApp) { return false; }
					if (m.onlyDesktop && isMobile) { return false; }
					if (m.onlyMobile && !isMobile) { return false; }
					return true;
				});

				console.log('getModalList=', rsvp.modals);

				if (!rsvp.modals) { return; }

				for (const modal of rsvp.modals) {
					let skipped = false;
					// Specjalny przypadek dla powiadomień Push - są one zależne od urządzenia.
					if (modal.name === 'pushNotifModal') {
						const permissionStatus = await getPermissionStatus('Permission.notification');
						let permissionDefault;
						if (isApp) {
							// Dla aplikacji mobilnej sprawdzamy wszystkie statusy, oprócz 'granted'.
							permissionDefault = permissionStatus !== 'granted';
						} else {
							// Dla web sprawdzamy tylko status 'default'.
							permissionDefault = permissionStatus === 'default';
						}
						console.log('pushNotificationModal=', { isMobile, permissionDefault });
						if (!permissionDefault || !isMobile) { skipped = true; }
					}

					// Dodaj do listy modali, które mogą być wyświetlone.
					state.tutorialModals.value.push({
						name: modal.name,
						regexp: new RegExp(modal.regexp),
						important: modal.important,
						displayed: false,
						skipped,
					});
				}
			} catch (err) {
				console.warn('getModalList', { err });
			}
		},

		/**
		 * Sprawdza czy można wyświetlić okno dialogowe
		 * @param pathname URL strony na której ma się wyświetlić okno
		 * @param modals Lista nazw okien
		 * @returns Nazwa okna do wyświetlenia, jeżeli można jakieś wyświetlić.
		 */
		canShowModal(modals: string[], pathname = '') {
			let timeoutValue = 15 * 60 * 1000; // 15 minut
			const customTimeoutValue = Number(window.localStorage.getItem('canShowModalTimeout'));
			if (customTimeoutValue) { timeoutValue = customTimeoutValue * 1000; }

			const { tutorialModals } = this;
			const pathnameToCheck = pathname || window.location.pathname;
			const wasAnyDisplayed = state.lastModalDate.value + timeoutValue > Number(new Date()); // Czy jakikolwiek inny był wyświetlony w ciągu ostatniego czasu?

			// Sprawdź wszystkie modale na ścieżce. Jeżeli nie ma żadnych, to wyjdź.
			const modalsOnPathAndNotDisplayed = tutorialModals.filter((m) => m.regexp.test(pathnameToCheck) && !m.displayed && !m.skipped);
			if (!modalsOnPathAndNotDisplayed.length) { return ''; }

			// Gdy jeszcze nie został wyświetlony żaden modal i jest w podanych w parametrach, to wyświetl pierwszy z listy.
			if (!wasAnyDisplayed) {
				const firstModalName = modalsOnPathAndNotDisplayed[0].name;
				console.log(
					'ModalManager: canShowModal',
					modals.includes(firstModalName),
					{
						modals, pathname, modalsOnPathAndNotDisplayed, firstModalName, wasAnyDisplayed,
					},
				);
				return modals.includes(firstModalName) ? firstModalName : '';
			}

			// W przeciwnym przypadku, sprawdź, czy któryś z modali jest ważny.
			const importantModalsOnPath = modalsOnPathAndNotDisplayed.filter((m) => m.important);
			if (!importantModalsOnPath.length) { return ''; }
			const firstImportantModalName = modalsOnPathAndNotDisplayed[0].name;
			return modals.includes(firstImportantModalName) ? firstImportantModalName : '';
		},

		/**
		 * Zapisuje informację o tym, że jakiś modal został wyświetlony podczas trwania tej sesji
		 * oraz ustawia datę ostatniego wyświetlenia.
		 */
		showModal(modalName: string, skipServerRequest = false) {
			const modal = state.tutorialModals.value.find((m) => m.name === modalName);
			if (modal) {
				console.log('ModalManager: showModal', modalName);
				modal.displayed = true;
			}

			const date = Number(new Date());
			state.lastModalDate.value = date;

			if (skipServerRequest) { return; }
			jsonFetch('/myoferteo/konto-json/modal/show-modal.do', {
				method: 'POST',
				body: { modalName },
			});
		},

		/**
		 * Ustawia flagę, czy dany modal może być pominięty, np. jeżeli zaktualizowaliśmy jakieś dane w sesji.
		 */
		skipModal(modalName: string, value: boolean, debugText = '') {
			const modal = state.tutorialModals.value.find((m) => m.name === modalName);
			if (modal) {
				console.log('ModalManager: skipModal', modalName, value, debugText);
				modal.skipped = value;
			}
		},
	};

	const getters = {};

	return { ...state, ...actions, ...getters };
});
